import { InvokeCallback, Sender } from "xstate";
import { withToken } from "@rentablo/api";
import {
  BookingsApiValidateManagedDepotBookingRequest,
  checkConsistencyStatus,
  validateCarveOut,
  validateManagedDepotBooking,
  validateSwitchInvestment,
} from "@rentablo/api/apiv1";
import { BookingValues } from "../types/bookingValues";
import { InvestmentValues } from "../types/investmentValues";
import { TransactionWizardContext } from "../types/transactionWizardContext";
import { TransactionWizardEvent } from "../types/TransactionWizardEvent";
import {
  buildBookingParams,
  buildCarveOutParams,
  buildInvestmentParams,
  buildInvestmentParamsList,
  buildManagedDepotParams,
  buildSwitchParams,
} from "../utils/buildRequestParams";
import { isCapitalMeasure } from "../utils/utils";

export const validateBooking =
  (
    context: TransactionWizardContext
  ): InvokeCallback<TransactionWizardEvent, TransactionWizardEvent> =>
  (callback) => {
    if (!context.investmentValues.depotId)
      throw new Error("no depot ID provided");
    if (!context.bookingValues.type) throw new Error("No booking type defined");
    if (
      isCapitalMeasure(context.bookingValues.type) &&
      !context.sourceInvestmentValues?.id
    )
      throw new Error("No source investment ID defined");

    if (context.investmentValues.type === "91_managed") {
      validateManagedDepot(
        callback,
        context.investmentValues,
        context.bookingValues
      );
      return;
    }

    isCapitalMeasure(context.bookingValues.type)
      ? validateCapitalMeasure(
          callback,
          context.sourceInvestmentValues?.id!,
          context.investmentValues,
          context.bookingValues
        )
      : validateStandard(
          callback,
          context.investmentValues,
          context.bookingValues
        );
  };

const validateStandard = async (
  callback: Sender<TransactionWizardEvent>,
  investmentValues: InvestmentValues,
  bookingValues: BookingValues
) => {
  try {
    const createOrUpdateInvestmentParamsList = buildInvestmentParamsList([
      buildInvestmentParams(investmentValues, [
        buildBookingParams(bookingValues),
      ]),
    ]);
    const result = await withToken(checkConsistencyStatus)({
      createOrUpdateInvestmentParamsList,
    });

    if (!result.hasValidationErrors) {
      callback({
        type: "BOOKING_VALID",
      });
      return;
    }

    callback({
      type: "BOOKING_INVALID",
      error:
        result.transactionValidationErrors.length > 0
          ? result.transactionValidationErrors[0].errorMessage
          : "Validation failed",
    });
  } catch (e) {
    callback({
      type: "SOMETHING_WRONG",
      error: e as AxiosApiError,
    });
  }
};

const validateCapitalMeasure = async (
  callback: Sender<TransactionWizardEvent>,
  sourceInvestmentId: number,
  investmentValues: InvestmentValues,
  bookingValues: BookingValues
) => {
  try {
    const investmentParams = buildInvestmentParams(investmentValues, [
      buildBookingParams(bookingValues),
    ]);

    const result =
      bookingValues.type === "carve_out"
        ? await withToken(validateCarveOut)({
            carveOutParams: buildCarveOutParams(
              sourceInvestmentId,
              investmentParams
            ),
          })
        : await withToken(validateSwitchInvestment)({
            switchParams: buildSwitchParams(
              sourceInvestmentId,
              investmentParams
            ),
          });

    if (!result.hasValidationErrors) {
      callback({
        type: "BOOKING_VALID",
      });
      return;
    }

    callback({
      type: "BOOKING_INVALID",
      error:
        result.transactionValidationErrors.length > 0
          ? result.transactionValidationErrors[0].errorMessage
          : "Validation failed",
    });
  } catch (e) {
    callback({
      type: "SOMETHING_WRONG",
      error: e as AxiosApiError,
    });
  }
};

const validateManagedDepot = async (
  callback: Sender<TransactionWizardEvent>,
  investmentValues: InvestmentValues,
  bookingValues: BookingValues
) => {
  try {
    const investmentParams = buildInvestmentParams(investmentValues, [
      buildBookingParams(bookingValues),
    ]);

    if (!bookingValues.entryQuote) throw new Error("no entry quote provided");

    const managedDepotParams = buildManagedDepotParams(
      bookingValues.entryQuote,
      bookingValues.depotValuationAfterBooking == null
        ? bookingValues.entryQuote
        : bookingValues.depotValuationAfterBooking,
      investmentParams
    );

    // The OpenAPI spec types this endpoint with CarveOutParams, but the
    // backend expects the managed-depot payload; cast at the boundary.
    const result = await withToken(validateManagedDepotBooking)({
      carveOutParams:
        managedDepotParams as unknown as BookingsApiValidateManagedDepotBookingRequest["carveOutParams"],
    });

    if (!result.hasValidationErrors) {
      callback({
        type: "BOOKING_VALID",
      });
      return;
    }

    callback({
      type: "BOOKING_INVALID",
      error:
        result.transactionValidationErrors.length > 0
          ? result.transactionValidationErrors[0].errorMessage
          : "Validation failed",
    });
  } catch (e) {
    callback({
      type: "SOMETHING_WRONG",
      error: e as AxiosApiError,
    });
  }
};
